import { useState, useRef } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding, BsThreeDots, BsTrash, BsX, BsCheckCircleFill, BsCheckCircle, BsCircle } from 'react-icons/bs';

import IconButton from '#globalComponents/IconButton/IconButton';
import Menu from '#globalComponents/Overlay/Menu';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import { truncateName } from '#lib/textUtils';

import CallReachedPopover from '../../components/common/CallReachedConfirm/CallReachedPopover';
import { getActivityType } from '../../constants/activityTypes';
import { isOverdue, isSending } from '../../utils/activityStatus';

function formatTime(d) {
	return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

const ActivityRowCheckbox = ({
	activity: a,
	checkboxRef,
	canComplete,
	completingId,
	onComplete,
	callReachedActivityId,
	onCallReachedSelect,
	onCallReachedCancel,
}) => (
	<>
		<button
			type="button"
			ref={checkboxRef}
			onClick={(e) => (canComplete || a.status === 'completed') && onComplete(e, a)}
			disabled={completingId === a.id}
			className={`flex-shrink-0 ${canComplete || a.status === 'completed' ? 'hover:scale-110 transition-transform' : ''}`}
		>
			{a.status === 'completed' ? (
				<BsCheckCircleFill className="w-4.5 h-4.5 text-success-500" />
			) : (
				<BsCircle
					className={`w-4.5 h-4.5 ${canComplete ? 'text-bgBase-800 hover:text-success-500' : 'text-bgBase-800'}`}
				/>
			)}
		</button>
		{callReachedActivityId === a.id && (
			<CallReachedPopover anchorRef={checkboxRef} onSelect={onCallReachedSelect} onCancel={onCallReachedCancel} />
		)}
	</>
);
ActivityRowCheckbox.propTypes = {
	activity: PropTypes.object,
	checkboxRef: PropTypes.object,
	canComplete: PropTypes.bool,
	completingId: PropTypes.string,
	onComplete: PropTypes.func,
	callReachedActivityId: PropTypes.string,
	onCallReachedSelect: PropTypes.func,
	onCallReachedCancel: PropTypes.func,
};

const AutomatedBadge = ({ sending }) => (
	<span
		className={`flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
			sending ? 'bg-primary-200 text-primary-700' : 'bg-primary-100 text-primary-700'
		}`}
		title={sending ? 'Versand läuft' : 'Automatisierter Versand'}
	>
		{sending ? 'Sendet…' : 'Auto'}
	</span>
);
AutomatedBadge.propTypes = { sending: PropTypes.bool };

const ActivityRowMenu = ({
	activity: a,
	active,
	members,
	menuOpen,
	setMenuOpen,
	onComplete,
	onCancelAutomated,
	onDelete,
	onReassign,
	isMeetingOrCalendar,
	isAutomated,
	isCampaign,
}) => (
	<div onClick={(e) => e.stopPropagation()}>
		<Popover
			open={menuOpen}
			onClose={() => setMenuOpen(false)}
			size="s"
			align="end"
			trigger={
				<IconButton
					icon={BsThreeDots}
					label="Aktionen"
					onClick={() => setMenuOpen(!menuOpen)}
					className="h-6 w-6 opacity-0 group-hover:opacity-100 transition"
					style={{ opacity: menuOpen || active ? 1 : undefined }}
				/>
			}
		>
			<Panel.Body padded={false}>
				<Menu>
					{!isMeetingOrCalendar && !isAutomated && (
						<Menu.Item
							icon={BsCheckCircle}
							onClick={() => {
								onComplete({ stopPropagation: () => {} }, a);
								setMenuOpen(false);
							}}
						>
							{a.status === 'completed' ? 'Wieder öffnen' : 'Als erledigt'}
						</Menu.Item>
					)}
					{isAutomated && a.status !== 'completed' && (
						<Menu.Item
							icon={BsX}
							tone="danger"
							onClick={() => {
								onCancelAutomated(a);
								setMenuOpen(false);
							}}
						>
							Abbrechen
						</Menu.Item>
					)}
					{!isMeetingOrCalendar && !isAutomated && !isCampaign && a.status !== 'completed' && (
						<>
							<Menu.Separator />
							<Menu.Label>Zuweisen</Menu.Label>
							{members.map((m) => (
								<Menu.Item
									key={m.userId}
									onClick={() => {
										onReassign(a.id, m.userId);
										setMenuOpen(false);
									}}
									trailing={
										a.userId === m.userId ? (
											<BsCheckCircleFill className="w-3 h-3 text-primary-500" />
										) : undefined
									}
								>
									<span className="flex items-center gap-2.5">
										<span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[9px] font-semibold flex-shrink-0">
											{m.fullName?.[0]?.toUpperCase()}
										</span>
										<span className="truncate">{m.fullName ?? m.email}</span>
									</span>
								</Menu.Item>
							))}
						</>
					)}
					{!isCampaign && (
						<>
							<Menu.Separator />
							<Menu.Item
								icon={BsTrash}
								tone="danger"
								onClick={() => {
									onDelete(a.id);
									setMenuOpen(false);
								}}
							>
								Löschen
							</Menu.Item>
						</>
					)}
				</Menu>
			</Panel.Body>
		</Popover>
	</div>
);
ActivityRowMenu.propTypes = {
	activity: PropTypes.object,
	active: PropTypes.bool,
	members: PropTypes.array,
	menuOpen: PropTypes.bool,
	setMenuOpen: PropTypes.func,
	onComplete: PropTypes.func,
	onCancelAutomated: PropTypes.func,
	onDelete: PropTypes.func,
	onReassign: PropTypes.func,
	isMeetingOrCalendar: PropTypes.bool,
	isAutomated: PropTypes.bool,
	isCampaign: PropTypes.bool,
};

const ActivityRow = ({
	activity: a,
	active,
	members,
	onClick,
	onComplete,
	onCancelAutomated,
	onDelete,
	onReassign,
	completingId,
	callReachedActivityId,
	onCallReachedSelect,
	onCallReachedCancel,
}) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const checkboxRef = useRef(null);
	const type = getActivityType(a.activityType);
	const Icon = type.icon;
	const dueDate = a.dueAt ? new Date(a.dueAt) : null;
	const isMeetingOrCalendar = a.activityType === 'meeting' || Boolean(a.calendarProvider);
	const isAutomated = Boolean(a.isAutomated);
	const isCampaign = Boolean(a.channelPayload?.campaignId);
	// A meeting on a past date is history, not a missed to-do.
	const overdue = !isMeetingOrCalendar && isOverdue(a);
	const sending = isSending(a);
	const canComplete = !isMeetingOrCalendar && !isAutomated && (a.status === 'planned' || a.status === 'in_progress');
	const ownerMember = members.find((m) => m.userId === a.userId);

	return (
		<div
			className={`group flex items-center gap-2.5 px-3 py-2.5 rounded-card mb-1 cursor-pointer transition-colors ${
				active
					? 'bg-primary-100 border border-primary-200'
					: 'bg-white border border-bgBase-500 hover:border-primary-200'
			} ${a.status === 'completed' ? 'opacity-60' : ''} ${overdue ? 'border-l-2 border-l-warning-500' : ''}`}
			onClick={onClick}
		>
			{isAutomated && a.status !== 'completed' ? (
				<AutomatedBadge sending={sending} />
			) : (
				<ActivityRowCheckbox
					activity={a}
					checkboxRef={checkboxRef}
					canComplete={canComplete}
					completingId={completingId}
					onComplete={onComplete}
					callReachedActivityId={callReachedActivityId}
					onCallReachedSelect={onCallReachedSelect}
					onCallReachedCancel={onCallReachedCancel}
				/>
			)}

			<div
				className="w-7 h-7 rounded-control flex items-center justify-center flex-shrink-0"
				style={{ background: type.color + '1a', color: type.color }}
			>
				<Icon className="w-3.5 h-3.5" />
			</div>

			{/* Main */}
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-1.5">
					{isAutomated && a.status !== 'completed' && (
						<span className="text-[10px] font-semibold text-primary-600">[Auto]</span>
					)}
					<span
						className={`text-[13px] font-semibold truncate ${a.status === 'completed' ? 'line-through text-bgBase-800' : 'text-text'}`}
						title={a.title}
					>
						{truncateName(a.title)}
					</span>
				</div>
				<div className="flex items-center gap-2 mt-0.5">
					{a.crmCompanyId && (
						<span
							className="inline-flex items-center gap-1 text-[10px] text-bgBase-800 bg-bgBase-400 rounded-full px-1.5 py-0.5"
							title={a.companyName ?? undefined}
						>
							<BsBuilding className="w-2.5 h-2.5 flex-shrink-0" />{' '}
							{truncateName(a.companyName) ?? 'Firma'}
						</span>
					)}
				</div>
			</div>

			{/* Time */}
			<div className="flex flex-col items-end flex-shrink-0 gap-0.5">
				{dueDate && (
					<span className={`text-[11px] font-medium ${overdue ? 'text-warning-500' : 'text-bgBase-800'}`}>
						{formatTime(dueDate)}
					</span>
				)}
				{overdue && (
					<span className="text-[9px] font-semibold text-warning-700 bg-warning-200 rounded-full px-1.5 py-0.5">
						{Math.floor((new Date() - dueDate) / 86400000)}d überfällig
					</span>
				)}
			</div>

			{/* Owner avatar */}
			{ownerMember && (
				<div
					className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[9px] font-semibold flex-shrink-0"
					title={ownerMember.fullName}
				>
					{ownerMember.fullName?.[0]?.toUpperCase() ?? '?'}
				</div>
			)}

			{/* More menu */}
			<ActivityRowMenu
				activity={a}
				active={active}
				members={members}
				menuOpen={menuOpen}
				setMenuOpen={setMenuOpen}
				onComplete={onComplete}
				onCancelAutomated={onCancelAutomated}
				onDelete={onDelete}
				onReassign={onReassign}
				isMeetingOrCalendar={isMeetingOrCalendar}
				isAutomated={isAutomated}
				isCampaign={isCampaign}
			/>
		</div>
	);
};

ActivityRow.propTypes = {
	activity: PropTypes.object,
	active: PropTypes.bool,
	members: PropTypes.array,
	onClick: PropTypes.func,
	onComplete: PropTypes.func,
	onCancelAutomated: PropTypes.func,
	onDelete: PropTypes.func,
	onReassign: PropTypes.func,
	completingId: PropTypes.string,
	callReachedActivityId: PropTypes.string,
	onCallReachedSelect: PropTypes.func,
	onCallReachedCancel: PropTypes.func,
};

export default ActivityRow;
